import { applySettings } from './apply.ts';
import { getIsTestMode, initTestMode } from './test-mode.ts';
import type { WhitelabelSettings } from './model.ts';
import type { WhitelabelConfig } from '@ringba/config';
import type { ILocation, IBrowserStorage } from './types.ts';

// Warning: should be no runtime dependencies script for fetching whitelabel data
// `window` is used only for local/session storages and location, and can be replaced with a mock or custom implementation

export interface WhiteLabelInputSettings {
  /** prefix for the cache key where settings will be stored in localStorage */
  cacheKey: string;
  /** settings for whitelabel origins */
  settings: WhitelabelConfig;
  /** default base path for the application */
  defaultBasePath: string;
  /** function to fetch whitelabel settings */
  fetcher: (friendlyName: string) => Promise<WhitelabelSettings>;
  /** location object to use instead of window.location */
  location?: ILocation;
  /** localStorage object to use instead of window.localStorage */
  localStorage?: IBrowserStorage;
  /** sessionStorage object to use instead of window.sessionStorage */
  sessionStorage?: IBrowserStorage;
  /** if true, skip applying settings */
  skipApply?: boolean;
  /** if true, additional logs will be printed */
  verbose?: boolean;
}

interface WhitelabelCache {
  friendlyName: string;
  timestamp: number;
  settings: WhitelabelSettings;
}

interface WhitelabelContext {
  args: WhiteLabelInputSettings;
  info: NonNullable<ReturnType<typeof getFriendlyName>>;
  data: WhitelabelCache | null;
  cacheKey: string;
  localStorage: IBrowserStorage;
  log: (...args: unknown[]) => void;
}

export interface WhitelabelResult {
  /** detected friendly name (path segment or hostname), or null if no WL detected */
  friendlyName: string | null;
  /** updated base path or null if no whitelabel settings were detected */
  path: string | null;
  /** whitelabel settings that will be or has been applied */
  settings: Promise<null | WhitelabelSettings>;
}

const CACHE_EXPIRATION = 1000 * 60 * 5; // 5 minutes

export function fetchAndApplyWhitelabel(args: WhiteLabelInputSettings): WhitelabelResult {
  initTestMode(args.settings.test_key, args.sessionStorage, args.location);

  const log = args.verbose
    // eslint-disable-next-line no-console
    ? (...logArgs: unknown[]) => console.log('[WL]', ...logArgs)
    : () => { /* no-op */ };

  const isTestMode = getIsTestMode(args.sessionStorage);
  const info = getFriendlyName(args, isTestMode);
  if (isTestMode && info) {
    info.test = true;
  }
  log('friendly name:', info);

  if (!info || !info.friendlyName) {
    return {
      friendlyName: null,
      path: null,
      settings: Promise.resolve(null),
    };
  }

  const ctx: WhitelabelContext = {
    args,
    info,
    data: null,
    cacheKey: `${args.cacheKey}_${info.friendlyName}`,
    localStorage: args.localStorage || window.localStorage,
    log,
  };

  let promise: Promise<null | WhitelabelSettings> | null = null;

  ctx.data = JSON.parse(ctx.localStorage.getItem(ctx.cacheKey) || 'null') as WhitelabelCache | null;
  if (ctx.data && ctx.data.friendlyName === info.friendlyName && ctx.data.settings) {
    // allow applying older settings until the new ones are fetched
    if (!args.skipApply) {
      applySettings(ctx.data.settings);
    }
    promise = Promise.resolve(ctx.data.settings);
  }

  const fetchPromise = tryFetchFresh(ctx);
  if (!promise) {
    promise = fetchPromise;
  }

  return {
    friendlyName: info.friendlyName,
    path: info.path,
    settings: promise,
  };
}

async function tryFetchFresh(ctx: WhitelabelContext): Promise<null | WhitelabelSettings> {
  const needFetch = !ctx.data
    || ctx.data.friendlyName !== ctx.info.friendlyName
    || !ctx.data.settings
    || !ctx.data.timestamp
    || Date.now() - ctx.data.timestamp > CACHE_EXPIRATION;

  if (!needFetch || !ctx.info.friendlyName) {
    return null;
  }

  try {
    const settings = await ctx.args.fetcher(ctx.info.friendlyName);

    const newData: WhitelabelCache = {
      friendlyName: ctx.info.friendlyName,
      timestamp: Date.now(),
      settings,
    };

    ctx.localStorage.setItem(ctx.cacheKey, JSON.stringify(newData));
    if (!ctx.args.skipApply) {
      applySettings(settings);
    }

    return settings;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[WHITELABEL] Failed to fetch whitelabel settings:', err);
  }

  return null;
}

function checkLocationHostIsBase(hostname: string, settings: WhitelabelConfig): boolean {
  return (settings.commonHostnames || []).every((commonHostname) => !hostname.includes(commonHostname))
    && (settings.stoplist || []).some((stopHostName) => hostname.includes(stopHostName));
}

interface FriendlyNameInfo {
  friendlyName: string | null;
  path: string | null;
  reason?: string;
  test?: boolean;
}

function getFriendlyName(
  { settings, defaultBasePath, location = window.location }: WhiteLabelInputSettings,
  isTestMode?: boolean,
): null | FriendlyNameInfo {
  const { hostname, pathname } = location;

  if (!isTestMode && checkLocationHostIsBase(hostname, settings)) {
    return {
      friendlyName: null,
      path: null,
      reason: 'not applicable because hostname is base',
    };
  }

  if (isTestMode || (settings.commonHostnames || []).some(domain => hostname.includes(domain))) {
    const cleanBasePath = defaultBasePath.replace(/\//g, '');

    const firstPathName = pathname.split('/').find(s => !!s);
    if (!firstPathName) {
      return {
        friendlyName: null,
        path: null,
        reason: 'path is empty',
      };
    }

    if (firstPathName === cleanBasePath) {
      return {
        friendlyName: null,
        path: null,
        reason: 'not applicable because path is base',
      };
    }

    return {
      friendlyName: firstPathName,
      path: firstPathName,
      reason: 'by first path segment',
    };
  }

  return {
    friendlyName: hostname,
    path: null,
    reason: 'by hostname',
  };
}
